(() => {
  const HERO_TAG = "minaco-hero";
  const MOBILE_BREAKPOINT = 749;
  const SWIPE_THRESHOLD = 48;
  const SWIPE_AXIS_RATIO = 1.15;
  const INTERACTION_IDLE_MS = 4000;

  class MinacoHero extends HTMLElement {
    constructor() {
      super();

      this.timer = null;
      this.transitionTimer = null;
      this.pendingTransition = null;
      this.isTransitioning = false;
      this.interactionResumeTimer = null;
      this.slides = [];
      this.indicators = [];
      this.currentIndex = 0;
      this.initialized = false;
      this.editorPaused = false;
      this.interactionPaused = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchActive = false;

      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleButtonFocus = this.handleButtonFocus.bind(this);
      this.handleButtonBlur = this.handleButtonBlur.bind(this);
      this.handleIndicatorClick = this.handleIndicatorClick.bind(this);
      this.handleIndicatorFocus = this.handleIndicatorFocus.bind(this);
      this.handleIndicatorBlur = this.handleIndicatorBlur.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    initialize() {
      if (this.initialized) return;

      this.initialized = true;
      this.slides = Array.from(this.querySelectorAll("[data-slide]"));
      this.indicators = Array.from(this.querySelectorAll("[data-hero-indicator]"));
      this.button = this.querySelector("[data-hero-button]");
      this.slidesContainer = this.querySelector(".minaco-hero__slides");
      this.intervalMs = Math.max(Number(this.dataset.interval) || 2000, 2000);
      this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

      const activeIndex = this.slides.findIndex((slide) =>
        slide.classList.contains("is-active"),
      );

      this.currentIndex = activeIndex >= 0 ? activeIndex : 0;

      if (this.button) {
        this.button.addEventListener("focus", this.handleButtonFocus);
        this.button.addEventListener("blur", this.handleButtonBlur);
      }

      this.indicators.forEach((indicator) => {
        indicator.addEventListener("click", this.handleIndicatorClick);
        indicator.addEventListener("focus", this.handleIndicatorFocus);
        indicator.addEventListener("blur", this.handleIndicatorBlur);
      });

      if (this.slidesContainer) {
        this.slidesContainer.addEventListener("touchstart", this.handleTouchStart, {
          passive: true,
        });
        this.slidesContainer.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.slidesContainer.addEventListener("touchcancel", this.handleTouchCancel, {
          passive: true,
        });
      }

      document.addEventListener("visibilitychange", this.handleVisibility);
      document.addEventListener("shopify:block:select", this.handleBlockSelect);
      document.addEventListener(
        "shopify:block:deselect",
        this.handleBlockDeselect,
      );
      document.addEventListener(
        "shopify:section:select",
        this.handleSectionSelect,
      );

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener("change", this.handleMotionChange);
      }

      this.showSlide(this.currentIndex, true);
      this.prepareNextImage();

      window.requestAnimationFrame(() => {
        this.start();
      });
    }

    destroy() {
      if (!this.initialized) return;

      this.stop();

      if (this.transitionTimer !== null) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }

      if (this.interactionResumeTimer !== null) {
        window.clearTimeout(this.interactionResumeTimer);
        this.interactionResumeTimer = null;
      }

      if (this.button) {
        this.button.removeEventListener("focus", this.handleButtonFocus);
        this.button.removeEventListener("blur", this.handleButtonBlur);
      }

      this.indicators.forEach((indicator) => {
        indicator.removeEventListener("click", this.handleIndicatorClick);
        indicator.removeEventListener("focus", this.handleIndicatorFocus);
        indicator.removeEventListener("blur", this.handleIndicatorBlur);
      });

      if (this.slidesContainer) {
        this.slidesContainer.removeEventListener("touchstart", this.handleTouchStart);
        this.slidesContainer.removeEventListener("touchend", this.handleTouchEnd);
        this.slidesContainer.removeEventListener("touchcancel", this.handleTouchCancel);
      }

      document.removeEventListener("visibilitychange", this.handleVisibility);
      document.removeEventListener(
        "shopify:block:select",
        this.handleBlockSelect,
      );
      document.removeEventListener(
        "shopify:block:deselect",
        this.handleBlockDeselect,
      );
      document.removeEventListener(
        "shopify:section:select",
        this.handleSectionSelect,
      );

      if (this.motionQuery && this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener("change", this.handleMotionChange);
      }

      this.slides = [];
      this.indicators = [];
      this.button = null;
      this.slidesContainer = null;
      this.initialized = false;
      this.editorPaused = false;
      this.interactionPaused = false;
      this.interactionResumeTimer = null;
      this.pendingTransition = null;
      this.isTransitioning = false;
      this.touchActive = false;
    }

    isDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    isMobile() {
      return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    }

    shouldPause() {
      return (
        this.slides.length < 2 ||
        document.hidden ||
        this.editorPaused ||
        this.interactionPaused
      );
    }

    start() {
      this.stop();

      if (this.shouldPause()) return;

      this.timer = window.setInterval(() => {
        const nextIndex = (this.currentIndex + 1) % this.slides.length;
        this.showSlide(nextIndex, false, "next");
      }, this.intervalMs);
    }

    stop() {
      if (this.timer === null) return;

      window.clearInterval(this.timer);
      this.timer = null;
    }

    showSlide(index, force = false, direction = "next") {
      const nextSlide = this.slides[index];

      if (!nextSlide) return;
      if (!force && index === this.currentIndex) return;

      if (!force && this.isTransitioning) {
        this.pendingTransition = { index, direction };
        return;
      }

      if (force && this.transitionTimer !== null) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
        this.pendingTransition = null;
        this.isTransitioning = false;
      }

      if (force) {
        this.classList.add("is-instant");

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;

          slide.classList.toggle("is-active", isActive);
          slide.classList.remove(
            "is-entering",
            "is-animating",
            "is-leaving",
            "from-next",
            "from-prev",
            "to-next",
            "to-prev",
          );
          slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        });

        this.currentIndex = index;
        this.updateIndicators();
        this.prepareNextImage();

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.classList.remove("is-instant");
          });
        });

        return;
      }

      const currentSlide = this.slides[this.currentIndex];
      const isPrevious = direction === "prev";
      const enterClass = isPrevious ? "from-prev" : "from-next";
      const leaveClass = isPrevious ? "to-prev" : "to-next";

      this.isTransitioning = true;

      /*
       * Stage the incoming slide off-canvas before starting the transition.
       * This extra render step is essential when the swipe direction reverses:
       * without it, the browser can apply the new direction and the active
       * transform in the same frame, briefly exposing two cropped slides.
       */
      this.slides.forEach((slide) => {
        if (slide !== currentSlide && slide !== nextSlide) {
          slide.classList.remove(
            "is-active",
            "is-entering",
            "is-animating",
            "is-leaving",
            "from-next",
            "from-prev",
            "to-next",
            "to-prev",
          );
          slide.setAttribute("aria-hidden", "true");
        }
      });

      nextSlide.classList.remove(
        "is-active",
        "is-leaving",
        "is-animating",
        "from-next",
        "from-prev",
        "to-next",
        "to-prev",
      );
      nextSlide.classList.add("is-entering", enterClass);
      nextSlide.setAttribute("aria-hidden", "false");

      // Force the staged off-screen position to be committed before motion.
      void nextSlide.offsetWidth;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (currentSlide) {
            currentSlide.classList.remove(
              "is-active",
              "is-entering",
              "is-animating",
              "from-next",
              "from-prev",
              "to-next",
              "to-prev",
            );
            currentSlide.classList.add("is-leaving", leaveClass);
            currentSlide.setAttribute("aria-hidden", "true");
          }

          nextSlide.classList.add("is-animating");

          this.currentIndex = index;
          this.updateIndicators();
          this.prepareNextImage();

          this.transitionTimer = window.setTimeout(() => {
            if (currentSlide) {
              currentSlide.classList.remove(
                "is-leaving",
                "to-next",
                "to-prev",
              );
            }

            nextSlide.classList.remove(
              "is-entering",
              "is-animating",
              "from-next",
              "from-prev",
            );
            nextSlide.classList.add("is-active");

            this.transitionTimer = null;
            this.isTransitioning = false;

            const pendingTransition = this.pendingTransition;
            this.pendingTransition = null;

            if (pendingTransition) {
              window.requestAnimationFrame(() => {
                this.showSlide(
                  pendingTransition.index,
                  false,
                  pendingTransition.direction,
                );
              });
            }
          }, 800);
        });
      });
    }

    showSlideFromIndicator(index) {
      const nextSlide = this.slides[index];
      const currentSlide = this.slides[this.currentIndex];

      if (!nextSlide || !currentSlide || index === this.currentIndex) return;
      if (this.isMobile()) {
        this.showSlide(index, false, index < this.currentIndex ? "prev" : "next");
        return;
      }

      if (this.transitionTimer !== null) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }

      const existingLayer = this.querySelector("[data-indicator-transition-layer]");
      if (existingLayer) existingLayer.remove();

      this.pendingTransition = null;
      this.isTransitioning = true;

      const targetVisual = nextSlide.querySelector(
        ".minaco-hero__picture, .minaco-hero__placeholder",
      );

      if (!targetVisual || !this.slidesContainer) {
        this.showSlide(index, true);
        this.isTransitioning = false;
        return;
      }

      /*
       * Desktop indicator clicks use a temporary photography-only layer.
       * The active slide and the overlay content never move or fade, so the
       * headline, description, CTA and pagination remain permanently visible.
       */
      const transitionLayer = document.createElement("div");
      transitionLayer.className = "minaco-hero__indicator-transition-layer";
      transitionLayer.dataset.indicatorTransitionLayer = "true";
      transitionLayer.setAttribute("aria-hidden", "true");

      const visualClone = targetVisual.cloneNode(true);
      const clonedImage = visualClone.querySelector && visualClone.querySelector("img");

      if (clonedImage) {
        clonedImage.loading = "eager";
        clonedImage.removeAttribute("fetchpriority");
      }

      transitionLayer.appendChild(visualClone);

      /*
       * Mount the photography layer directly on the hero, never inside the
       * slides track. This guarantees that the permanent overlay UI remains
       * in its own higher stacking layer and is never repainted/replaced by
       * the image transition.
       */
      const contentLayer = this.querySelector(".minaco-hero__content");
      if (contentLayer) {
        this.insertBefore(transitionLayer, contentLayer);
      } else {
        this.appendChild(transitionLayer);
      }

      // Commit the initial hidden state before starting the soft reveal.
      void transitionLayer.offsetWidth;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          transitionLayer.classList.add("is-visible");
          this.updateIndicatorSelection(index);

          this.transitionTimer = window.setTimeout(() => {
            /*
             * Swap only the underlying photography state. Do not toggle any
             * root-level class here: the headline, copy, CTA and pagination
             * must remain completely untouched throughout indicator clicks.
             */
            this.slides.forEach((slide, slideIndex) => {
              const isActive = slideIndex === index;

              slide.classList.toggle("is-active", isActive);
              slide.classList.remove(
                "is-entering",
                "is-animating",
                "is-leaving",
                "from-next",
                "from-prev",
                "to-next",
                "to-prev",
              );
              slide.setAttribute("aria-hidden", isActive ? "false" : "true");
            });

            this.currentIndex = index;
            this.prepareNextImage();

            window.requestAnimationFrame(() => {
              /*
               * The target slide is already active underneath with the exact
               * same image, so removing the temporary layer is visually
               * seamless and does not affect the permanent content layer.
               */
              transitionLayer.remove();
              this.transitionTimer = null;
              this.isTransitioning = false;
            });
          }, 460);
        });
      });
    }

    updateIndicatorSelection(index) {
      this.indicators.forEach((indicator, indicatorIndex) => {
        const isActive = indicatorIndex === index;

        indicator.classList.toggle("is-active", isActive);
        indicator.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    updateIndicators() {
      this.updateIndicatorSelection(this.currentIndex);
    }

    prepareNextImage() {
      if (this.slides.length < 2) return;

      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      const nextImage = this.slides[nextIndex].querySelector("img");

      if (!nextImage) return;

      if (nextImage.loading === "lazy") {
        nextImage.loading = "eager";
      }

      if (typeof nextImage.decode === "function") {
        nextImage.decode().catch(() => {});
      }
    }

    pauseForInteraction() {
      this.interactionPaused = true;
      this.stop();

      if (this.interactionResumeTimer !== null) {
        window.clearTimeout(this.interactionResumeTimer);
        this.interactionResumeTimer = null;
      }
    }

    resumeAfterInteractionDelay() {
      if (this.interactionResumeTimer !== null) {
        window.clearTimeout(this.interactionResumeTimer);
      }

      this.interactionResumeTimer = window.setTimeout(() => {
        this.interactionResumeTimer = null;
        this.interactionPaused = false;
        this.start();
      }, INTERACTION_IDLE_MS);
    }

    showSlideInstant(index) {
      this.showSlide(index, true);
    }

    handleVisibility() {
      if (document.hidden) {
        this.stop();
        return;
      }

      this.start();
    }

    handleMotionChange() {
      this.start();
    }

    handleButtonFocus() {
      if (this.isDesignMode()) return;

      this.interactionPaused = true;
      this.stop();
    }

    handleButtonBlur() {
      this.resumeAfterInteractionDelay();
    }

    handleIndicatorClick(event) {
      const indicator = event.currentTarget;
      const index = Number(indicator.dataset.slideIndex);

      if (!Number.isInteger(index)) return;

      this.pauseForInteraction();

      if (index !== this.currentIndex) {
        this.showSlideFromIndicator(index);
      }

      this.resumeAfterInteractionDelay();
    }

    handleIndicatorFocus() {
      if (this.isDesignMode()) return;

      this.pauseForInteraction();
    }

    handleIndicatorBlur() {
      this.resumeAfterInteractionDelay();
    }

    handleTouchStart(event) {
      if (!this.isMobile() || this.slides.length < 2 || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchActive = true;
      this.pauseForInteraction();
    }

    handleTouchEnd(event) {
      if (!this.touchActive) return;

      this.touchActive = false;

      const touch = event.changedTouches && event.changedTouches[0];

      if (touch) {
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        const isHorizontalSwipe =
          Math.abs(deltaX) >= SWIPE_THRESHOLD &&
          Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO;

        if (isHorizontalSwipe) {
          if (deltaX < 0) {
            const nextIndex = (this.currentIndex + 1) % this.slides.length;
            this.showSlide(nextIndex, false, "next");
          } else {
            const previousIndex =
              (this.currentIndex - 1 + this.slides.length) % this.slides.length;
            this.showSlide(previousIndex, false, "prev");
          }
        }
      }

      this.resumeAfterInteractionDelay();
    }

    handleTouchCancel() {
      if (!this.touchActive) return;

      this.touchActive = false;
      this.resumeAfterInteractionDelay();
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const selectedSlide = event.target.closest("[data-slide]");

      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const selectedIndex = this.slides.indexOf(selectedSlide);

      if (selectedIndex < 0) return;

      this.editorPaused = true;
      this.stop();
      this.showSlide(selectedIndex, true);
    }

    handleBlockDeselect(event) {
      if (!(event.target instanceof Element)) return;

      const selectedSlide = event.target.closest("[data-slide]");

      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.editorPaused = false;
      this.start();
    }

    handleSectionSelect(event) {
      if (!(event.target instanceof Element)) return;
      if (!event.target.contains(this)) return;

      this.editorPaused = false;
      this.start();
    }
  }

  if (!customElements.get(HERO_TAG)) {
    customElements.define(HERO_TAG, MinacoHero);
  }

  function initializeHeroes(root = document) {
    if (root instanceof Element && root.matches(HERO_TAG)) {
      root.initialize();
    }

    if (!root.querySelectorAll) return;

    root.querySelectorAll(HERO_TAG).forEach((hero) => {
      hero.initialize();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeHeroes(document),
      { once: true },
    );
  } else {
    initializeHeroes(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeHeroes(event.target);
  });
})();
